import * as uuid from 'uuid'
import { reducerWithInitialState } from 'typescript-fsa-reducers'

import WorldState, { Message, Player, isImage } from '../states/WorldState'
import { EventHub, Photo, World } from '../actions'
import {
  LoadDataEvent,
  isAbsentEvent,
  isMapObjectEventUpdate,
  isMoveEvent,
  isPresentEvent,
  isSpeakEvent
} from '../io/events'
import { pointEquals } from '../models/BasicTypes'

export const initialState: WorldState = {
  config: null,
  blocks: [],
  messages: [],
  pendingMessages: [],
  player: null,
  mapObjects: {},
  events: [],
  viewPort: { origin: { x: 0, y: 0 }, size: { width: 0, height: 0 } }
}

export default reducerWithInitialState(initialState)
  .case(World.viewPortUpdated, (state, viewPort) => ({
    ...state,
    viewPort
  }))
  .case(World.updateBlock, (state, { add, remove }) => ({
    ...state,
    blocks: [
      ...state.blocks.filter(
        // TODO: this is slow
        block =>
          remove.findIndex(toDelete => pointEquals(block, toDelete)) === -1
      ),
      ...add
    ]
  }))
  .case(World.speakMessageReceived, (state, message) => ({
    ...state,
    messages: [...state.messages, message]
    // TODO: cap a count of messages
  }))
  .case(World.join.done, (state, payload) => ({
    ...state,
    player: payload.result
  }))
  .case(World.loadConfig.done, (state, payload) => ({
    ...state,
    config: payload.result
  }))
  .case(World.loadMapObject.done, (state, payload) => {
    let mapObjects = state.mapObjects
    let events = state.events
    if (payload.result.id in mapObjects) {
      const mapObjectState = mapObjects[payload.result.id]
      mapObjects = {
        ...mapObjects,
        [payload.result.id]: {
          ...mapObjectState,
          mapObject: {
            ...mapObjectState.mapObject,
            ...payload.result
          },
          loaded: true
        }
      }
      const eventID = uuid()
      const event: LoadDataEvent = {
        id: `LOCAL${eventID}`,
        mapObjectID: payload.result.id,
        type: 'load',
        data: payload.result,
        createdAt: new Date().getTime(),
        local: true,
        bySelf: false
      }
      events = [...events, event]
    }
    return {
      ...state,
      mapObjects,
      events
    }
  })
  .case(EventHub.eventUpdate, (state, update) => {
    let { mapObjects, events, messages, pendingMessages } = state
    if (!isMapObjectEventUpdate(update)) {
      return state
    }
    const { event } = update
    if (!(event.mapObjectID in mapObjects)) {
      return state
    }
    let addEvent = true
    // update position
    if (isMoveEvent(event)) {
      const mapObjectState = mapObjects[event.mapObjectID]
      mapObjects = {
        ...mapObjects,
        [event.mapObjectID]: {
          ...mapObjectState,
          mapObject: {
            ...mapObjectState.mapObject,
            position: event.source,
            lastMoveEvent: {
              source: event.source,
              destination: event.destination,
              createdAt: event.createdAt,
              endsAt: event.endsAt
            }
          } as Player
        }
      }
    } else if (isSpeakEvent(event)) {
      // TODO: what if sent by other user?
      const index = pendingMessages.findIndex(
        msg => msg.eventID === event.localID
      )
      const newMessage = {
        eventID: event.id,
        message: event.message,
        userID: 'FIXME',
        userName: event.name,
        createdAt: event.createdAt
      }
      if (index !== -1) {
        messages = messages.map(msg => {
          if (msg.eventID === event.localID) {
            return newMessage
          }
          return msg
        })
        pendingMessages = [
          ...pendingMessages.slice(0, index),
          ...pendingMessages.slice(index + 1)
        ]
      } else {
        // TODO: cap size here
        messages = [...messages, newMessage]
      }
      // set map object as present
    } else if (isPresentEvent(event)) {
      const mapObjectState = mapObjects[event.mapObjectID]
      mapObjects = {
        ...mapObjects,
        [event.mapObjectID]: {
          ...mapObjectState,
          mapObject: {
            ...mapObjectState.mapObject,
            position: event.position
          },
          present: true
        }
      }
      addEvent = !event.migration
    } else if (isAbsentEvent(event)) {
      addEvent = !event.migration
    }
    if (addEvent) {
      events = [...events, event]
    }
    return {
      ...state,
      events,
      mapObjects,
      messages,
      pendingMessages
    }
  })
  .case(World.localEventUpdate, (state, event) => {
    let { mapObjects, events, messages, pendingMessages } = state
    if (!(event.mapObjectID in mapObjects)) {
      return state
    }
    // update position
    if (isMoveEvent(event)) {
      const mapObjectState = mapObjects[event.mapObjectID]
      mapObjects = {
        ...mapObjects,
        [event.mapObjectID]: {
          ...mapObjectState,
          mapObject: {
            ...mapObjectState.mapObject,
            position: event.source,
            lastMoveEvent: {
              source: event.source,
              destination: event.destination,
              createdAt: event.createdAt,
              endsAt: event.endsAt
            }
          } as Player
        }
      }
    } else if (isSpeakEvent(event)) {
      // TODO: cap the msg size, ignore event sent created by local?
      const message: Message = {
        eventID: event.id,
        message: event.message,
        userID: 'FIXME',
        userName: event.name,
        createdAt: event.createdAt
      }
      messages = [...messages, message]
      pendingMessages = [...pendingMessages, message]
    }
    events = [...events, event]
    return {
      ...state,
      events,
      mapObjects,
      messages,
      pendingMessages
    }
  })
  .case(World.addMapObject, (state, mapObject) => ({
    ...state,
    mapObjects: {
      ...state.mapObjects,
      [mapObject.id]: {
        mapObject,
        present: false,
        loaded: false
      }
    }
  }))
  .case(World.removeMapObject, (state, mapObjectID) => {
    let mapObjects = state.mapObjects
    if (mapObjectID in mapObjects) {
      mapObjects = { ...mapObjects }
      delete mapObjects[mapObjectID]
    }
    return {
      ...state,
      mapObjects
    }
  })
  .case(World.eventProcessed, (state, lastID) => {
    const { events } = state
    const index = events.findIndex(event => event.id === lastID)
    if (index === -1) {
      throw new Error(`event processed returned a non-existing id ${lastID}`)
    }
    return {
      ...state,
      events: events.splice(index + 1)
    }
  })
  .case(Photo.updatePhoto.started, (state, { id, loved }) => {
    const mapObjectState = state.mapObjects[id]
    if (!mapObjectState) {
      return state
    }
    const image = mapObjectState.mapObject
    if (!isImage(image)) {
      return state
    }
    const newImage = {
      ...image,
      loved
    }
    return {
      ...state,
      mapObjects: {
        ...state.mapObjects,
        [id]: {
          ...mapObjectState,
          mapObject: newImage
        }
      }
    }
  })
  .build()



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/reducers/world.ts